import {
  Box,
  Column,
  Columns,
  IconArrow,
  IconClear,
  Inline,
  Text,
  TextLinkButton,
} from 'braid-design-system';
import { createContext, useContext, useEffect, useState } from 'react';

const ONE_DAY_MS = 1000 * 60 * 60 * 24;

const Context = createContext<[boolean, () => void] | null>([false, () => {}]);

export const PreviewDeploymentBannerProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [isPreview, setIsPreview] = useState(false);
  const [dismissedRecently, setDismissedRecently] = useState(false);

  // useEffect to run client-side only
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const version = searchParams.get('v');

    if (version) {
      setIsPreview(true);
    }

    const dismissedDate = window.localStorage.getItem(
      `seekDeveloperDismissed.v_${version}`,
    );

    if (dismissedDate) {
      const elapsed = new Date().getTime() - new Date(dismissedDate).getTime();
      if (elapsed < ONE_DAY_MS) {
        setDismissedRecently(true);
      }
    }
  }, [setIsPreview]);

  const dismiss = () => {
    setDismissedRecently(true);

    const searchParams = new URLSearchParams(window.location.search);
    const version = searchParams.get('v');

    window.localStorage.setItem(
      `seekDeveloperDismissed.v_${version}`,
      new Date().toISOString(),
    );
  };

  return (
    <Context.Provider value={[isPreview && !dismissedRecently, dismiss]}>
      {children}
    </Context.Provider>
  );
};

export const useShowPreview = () => {
  const context = useContext(Context);
  if (!context) {
    throw new Error(
      'useShowPreview must be used within a PreviewDeploymentBannerProvider',
    );
  }

  return context;
};

export const PreviewDeploymentBanner = ({
  dismiss,
}: {
  dismiss: () => void;
}) => (
  <Box background="caution" padding="medium">
    <Columns alignY="center" collapseBelow="tablet" space="medium">
      <Column>
        <Text>
          This is a preview version of the SEEK Developer Site. Avoid sharing
          links to this version with third parties.
        </Text>
      </Column>
      <Column width="content">
        <Inline space="small">
          <Text>
            <TextLinkButton
              icon={<IconArrow direction="right" />}
              onClick={() => {
                const url = new URL(window.location.href);
                url.searchParams.delete('v');
                window.location.assign(url);
              }}
            >
              Live site
            </TextLinkButton>
          </Text>

          <Text>
            <TextLinkButton icon={<IconClear />} onClick={dismiss}>
              Dismiss
            </TextLinkButton>
          </Text>
        </Inline>
      </Column>
    </Columns>
  </Box>
);
